
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

export function useRetentionOperations() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [dryRunResults, setDryRunResults] = useState<any>(null);

  // Function to perform a dry run of retention processing
  const dryRunMutation = useMutation({
    mutationFn: async ({
      state,
      courseType,
      retentionDate
    }: {
      state?: string;
      courseType?: string;
      retentionDate: string;
    }) => {
      try {
        const { data, error } = await supabase.functions.invoke('dry_run_certificate_retention', {
          body: { state, courseType, retentionDate }
        });
        
        if (error) throw error;
        return data;
      } catch (error) {
        console.error('Error performing dry run:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      setDryRunResults(data);
      toast({
        title: 'Dry Run Complete',
        description: `Found ${data.certificates_count || 0} certificates that would be archived.`,
      });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Dry Run Failed',
        description: error.message || 'An unknown error occurred.',
      });
    }
  });

  // Function to execute retention processing
  const executeMutation = useMutation({
    mutationFn: async ({
      state,
      courseType,
      retentionDate
    }: {
      state?: string;
      courseType?: string;
      retentionDate: string;
    }) => {
      try {
        const { data, error } = await supabase.functions.invoke('execute_certificate_retention', {
          body: { state, courseType, retentionDate }
        });
        
        if (error) throw error;
        return data;
      } catch (error) {
        console.error('Error executing retention:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      // Reset the dry run results
      setDryRunResults(null);
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({queryKey: ['certificateInventory']});
      queryClient.invalidateQueries({queryKey: ['archivedCertificates']});
      queryClient.invalidateQueries({queryKey: ['retentionLogs']});
      
      toast({
        title: 'Retention Complete',
        description: `Successfully archived ${data.archived_count || 0} certificates.`,
      });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Retention Failed',
        description: error.message || 'An unknown error occurred.',
      });
    }
  });

  // Function to verify retention compliance
  const verifyMutation = useMutation({
    mutationFn: async ({
      state,
      courseType
    }: {
      state?: string;
      courseType?: string;
    }) => {
      try {
        const { data, error } = await supabase.functions.invoke('verify_certificate_retention', {
          body: { state, courseType }
        });
        
        if (error) throw error;
        return data;
      } catch (error) {
        console.error('Error verifying retention:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      const totalIssues = (data.issues_found || 0);
      
      if (totalIssues > 0) {
        toast({
          variant: 'default',
          title: 'Verification Complete',
          description: `Found ${totalIssues} compliance issues that need attention.`,
        });
      } else {
        toast({
          title: 'Verification Complete',
          description: 'No compliance issues found. All certificate retention policies are being followed.',
        });
      }
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Verification Failed',
        description: error.message || 'An unknown error occurred.',
      });
    }
  });

  return {
    dryRunResults,
    setDryRunResults,
    dryRunMutation,
    executeMutation,
    verifyMutation
  };
}
